import { Star, Trash2, X } from "lucide-react";
import { useState, useEffect } from "react";
import { getSignalRatingsAdmin, deleteSignalRating } from "../../../interfaces/signals";
import { ISignalRating } from "../../../interfaces/signal";

interface SignalRatingsModalProps {
    signalId: string;
    signalTitle: string;
    onClose: () => void;
}

export default function SignalRatingsModal({
    signalId,
    signalTitle,
    onClose,
}: SignalRatingsModalProps) {
    const [loading, setLoading] = useState(true);
    const [averageRating, setAverageRating] = useState(0);
    const [ratingCount, setRatingCount] = useState(0);
    const [ratings, setRatings] = useState<ISignalRating[]>([]);
    const [deletingRatingId, setDeletingRatingId] = useState<string | null>(null);

    useEffect(() => {
        const fetchRatings = async () => {
            try {
                setLoading(true);
                const data = await getSignalRatingsAdmin(signalId);
                setAverageRating(data.averageRating);
                setRatingCount(data.ratingCount);
                setRatings(data.ratings || []);
            } catch (error) {
                console.error("Failed to fetch ratings:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchRatings();
    }, [signalId]);

    const handleDeleteRating = async (userId: string) => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette évaluation ?")) {
            return;
        }

        try {
            setDeletingRatingId(userId);
            const result = await deleteSignalRating(signalId, userId);
            
            // Update local state
            setRatings(prev => prev.filter(r => r.userId !== userId));
            setAverageRating(result.averageRating);
            setRatingCount(result.ratingCount);
        } catch (error) {
            console.error("Failed to delete rating:", error);
            alert("Échec de la suppression de l'évaluation");
        } finally {
            setDeletingRatingId(null);
        }
    };

    const renderStars = (rating: number) => {
        return (
            <div className="flex items-center gap-0.5">
                {Array.from({ length: 5 }, (_, i) => (
                    <Star
                        key={i}
                        className={`h-4 w-4 ${
                            i < rating ? "text-yellow-400" : "text-gray-300"
                        }`}
                    />
                ))}
            </div>
        );
    };

    const getRatingDistribution = () => {
        const distribution = [0, 0, 0, 0, 0]; // 1-5 stars
        ratings.forEach((r) => {
            if (r.rating >= 1 && r.rating <= 5) {
                distribution[r.rating - 1]++;
            }
        });
        return distribution;
    };

    const distribution = getRatingDistribution();

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-xl">
                {/* Header */}
                <div className="flex items-start justify-between p-6 border-b border-gray-200">
                    <div className="flex-1">
                        <h2 className="text-xl font-bold text-navy-700 mb-1">
                            Évaluations du Signal
                        </h2>
                        <p className="text-sm text-gray-600 line-clamp-2">{signalTitle}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="ml-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="h-5 w-5 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto" style={{ maxHeight: "calc(90vh - 140px)" }}>
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy-700"></div>
                        </div>
                    ) : (
                        <>
                            {/* Overall Rating */}
                            <div className="bg-gray-50 rounded-lg p-6 mb-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="text-4xl font-bold text-navy-700 mb-2">
                                            {averageRating.toFixed(1)}
                                        </div>
                                        {renderStars(Math.round(averageRating))}
                                        <p className="text-sm text-gray-600 mt-2">
                                            {ratingCount} {ratingCount === 1 ? "évaluation" : "évaluations"}
                                        </p>
                                    </div>

                                    {/* Rating Distribution */}
                                    <div className="flex-1 max-w-xs ml-8">
                                        {[5, 4, 3, 2, 1].map((star) => {
                                            const count = distribution[star - 1];
                                            const percentage =
                                                ratingCount > 0 ? (count / ratingCount) * 100 : 0;
                                            return (
                                                <div
                                                    key={star}
                                                    className="flex items-center gap-2 mb-1"
                                                >
                                                    <span className="text-xs text-gray-600 w-8">
                                                        {star} ★
                                                    </span>
                                                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                                                        <div
                                                            className="bg-yellow-400 h-2 rounded-full transition-all"
                                                            style={{ width: `${percentage}%` }}
                                                        />
                                                    </div>
                                                    <span className="text-xs text-gray-600 w-8">
                                                        {count}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* Individual Ratings */}
                            <div>
                                <h3 className="text-lg font-bold text-navy-700 mb-4">
                                    Évaluations individuelles
                                </h3>

                                {ratings.length === 0 ? (
                                    <p className="text-gray-500 text-center py-8">
                                        Aucune évaluation pour ce signal
                                    </p>
                                ) : (
                                    <div className="space-y-3">
                                        {ratings
                                            .sort((a, b) => 
                                                new Date(b.ratedAt).getTime() - new Date(a.ratedAt).getTime()
                                            )
                                            .map((rating, index) => {
                                                const userInitials = rating.userEmail 
                                                    ? rating.userEmail.substring(0, 2).toUpperCase()
                                                    : rating.userId.substring(0, 2).toUpperCase();
                                                
                                                return (
                                                    <div
                                                        key={index}
                                                        className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-3 flex-1">
                                                                <div className="flex items-center justify-center w-8 h-8 bg-navy-700 text-white rounded-full text-sm font-medium flex-shrink-0">
                                                                    {userInitials}
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center gap-2">
                                                                        {renderStars(rating.rating)}
                                                                        <span className="text-sm font-medium text-navy-700">
                                                                            {rating.rating}/5
                                                                        </span>
                                                                    </div>
                                                                    <p className="text-xs text-gray-500 mt-1 truncate">
                                                                        {rating.userEmail ? (
                                                                            <>
                                                                                {rating.userName && (
                                                                                    <span className="font-medium text-gray-700">
                                                                                        {rating.userName} •{" "}
                                                                                    </span>
                                                                                )}
                                                                                {rating.userEmail}
                                                                            </>
                                                                        ) : (
                                                                            `User ID: ${rating.userId.substring(0, 8)}...`
                                                                        )}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-3 flex-shrink-0">
                                                                <div className="text-xs text-gray-500 text-right">
                                                                    {new Date(rating.ratedAt).toLocaleDateString("fr-FR", {
                                                                        year: "numeric",
                                                                        month: "short",
                                                                        day: "numeric",
                                                                        hour: "2-digit",
                                                                        minute: "2-digit",
                                                                    })}
                                                                </div>
                                                                <button
                                                                    onClick={() => handleDeleteRating(rating.userId)}
                                                                    disabled={deletingRatingId === rating.userId}
                                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                    title="Supprimer cette évaluation"
                                                                >
                                                                    {deletingRatingId === rating.userId ? (
                                                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                                                    ) : (
                                                                        <Trash2 className="h-4 w-4" />
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200 bg-gray-50">
                    <button
                        onClick={onClose}
                        className="w-full px-4 py-2 bg-navy-700 text-white rounded-lg hover:bg-navy-600 transition-colors font-medium"
                    >
                        Fermer
                    </button>
                </div>
            </div>
        </div>
    );
}
